import React from 'react';
import {useTranslation} from 'react-i18next';
import {createStructuredSelector} from 'reselect';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {selectDevices, selectDevicesFetched} from '../../redux/selectors/device.selector';
import FormControl from '@mui/material/FormControl';
import Checkbox from '../Controls/Checkbox';
import Alert from '../Alert';
import {forAnyObject, forConcerningObject} from '../../utils/models/Scenario';
import {capitalize} from 'lodash/string';
import {CircularProgress} from '@mui/material';
import {CheckBoxCheckedIcon, CheckBoxUncheckedIcon} from "../../utils/icons";
import MuiAutocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import {Flex} from "../Container/Flex";
import {selectCurrentUser} from "../../redux/selectors/app.selectors";
import {Text} from "../../utils/style/texts";
import {StatusDot} from "../../containers/AdminDevicePage/style";

/* eslint jsx-a11y/role-supports-aria-props: 0 */

const inputLabelRef = React.createRef();

// Helper to check if a value is a special selector (forAnyObject / forConcerningObject)
const isSpecialValue = v => v === forAnyObject || v === forConcerningObject;
// Helper to check if a value is a real device ID (numeric or UUID string)
const isDeviceId = v => v != null && !isSpecialValue(v);

const DeviceSelector = props => {
    let {
        user,
        value,
        fetched,
        renderValue,
        showStatus,
        label,
        hideOffline,
        disabled,
        hideDisabled,
        isDeviceTrigger,
        isSource,
        scenarioTrigger,
        incompatibleActionText,
        alsoConcerningDevice,
        appsOnly,
        // V4 targets: cuebly-mobile phones are apps too, and V4 devices span accounts.
        v4Apps,
        anyAccount,
        fullWidth,
        hideUsers,
        devices,
        ...other
    } = props;
    const multiple = props.multiple !== false;
    const accountIds = props.accountIds || [user.accountId];
    const inAccount = d => anyAccount || accountIds.indexOf(d?.accountId) >= 0;
    
    const onChange = newValue => {
        props.onChange(newValue.filter(v => {
            if(isSpecialValue(v)) return true;
            return inAccount(devices.find(d => String(d.id) === String(v)));
        }));
    };

    const [t] = useTranslation();
    if (appsOnly) {
        devices = devices.filter(d => d.isApp || (v4Apps && d.isCueblyMobile));
    }
    if (hideUsers) {
        devices = devices.filter(d => !d.isWebUser);
    }

    const disableOption = other.disableOption || (() => false);
    
    if (multiple && !Array.isArray(value)) {
        setTimeout(() => onChange([]));
        return null;
    }
    if(!multiple && !Array.isArray(value)) {
        value = [value];
    }

    if (value?.length) {
        value = value.filter(v => isSpecialValue(v) || devices.find(d => String(d.id) === String(v)));
    }

    const options = [];
    if(multiple) options.push({
        label: capitalize(isDeviceTrigger ? t('allOtherDevices') : isSource ? t('anyDevice') : t('allDevices')),
        value: forAnyObject
    });
    if (alsoConcerningDevice) {
        options.push({
            label: t('forConcerningDevice'),
            value: forConcerningObject
        });
    }
    const deviceMap = {};
    devices
        .filter(d => d.connected || !hideOffline)
        .filter(inAccount).forEach(d => {
        options.push({
            label: (
                <Flex row gap={6} align="center">
                    {showStatus && <StatusDot small color={d.color} />}
                    <Text>{accountIds?.length > 1 ? `${d.name} (${d.id})` : d.name}</Text>
                </Flex>
            ),
            value: d.id,
            device: d
        });
        deviceMap[d.id] = d;
    });

    const forAny = value.indexOf(forAnyObject) >= 0;
    const forConcerning = value.indexOf(forConcerningObject) >= 0;
    const defaultLabel = () => {
        if (value.indexOf(forAnyObject) >= 0) return capitalize(isDeviceTrigger ? t('allOtherDevices') : t('allDevices'));
        if (value.indexOf(forConcerningObject) >= 0) return capitalize(t('forConcerningDevice'));
        value = value.filter(v => isSpecialValue(v) || devices.find(d => String(d.id) === String(v)));
        if (value.length > 1) return t('devices.count', {count: value.length});
        if (value.length === 1) return (devices.find(d => d.id === value[0]) || {}).name;
        return '';
    }

    return (
        <Flex fullWidth={fullWidth}>
            {devices.size ? (
                <FormControl ref={inputLabelRef} fullWidth disabled={!!disabled}>
                    <MuiAutocomplete
                        multiple={multiple}
                        value={value}
                        renderOption={({dataOptionIndex, ...props}, o) => {
                            const device = deviceMap[o.value];
                            const optionDisabled = disabled || (isDeviceId(device?.id) && disableOption(device));
                            const checked =
                                value.indexOf(o.value) >= 0 ||
                                (forAny && o.value !== forConcerningObject && !optionDisabled) ||
                                (forConcerning && o.value === forConcerningObject && !optionDisabled);
                            return (
                                <li {...props}
                                    aria-selected={false}
                                    data-deviceid={o.value}
                                    data-checked={checked ? 'yes' : 'no'}
                                    style={{...props.style, display: hideDisabled && optionDisabled ? 'none' : undefined}}
                                >
                                    <Checkbox
                                        icon={<CheckBoxUncheckedIcon/>}
                                        checkedIcon={<CheckBoxCheckedIcon/>}
                                        disabled={optionDisabled}
                                        color={isDeviceId(o.value) ? 'primary' : 'secondary'}
                                        indeterminate={forAny && isDeviceId(o.value) && !optionDisabled}
                                        style={{marginRight: 8, pointerEvents: 'none'}}
                                        checked={checked}
                                    />
                                    <span style={{pointerEvents: 'none'}}>{o.label}</span>
                                </li>
                            )
                        }}
                        isOptionEqualToValue={(o, v) => value.indexOf(v) >= 0}
                        disabled={!!disabled}
                        disableCloseOnSelect
                        renderTags={() => renderValue ? renderValue() : defaultLabel()}
                        getOptionLabel={o => o.device?.filterString(t) || 'any'} 
                        groupBy={o => accountIds?.length > 1 ? o.device?.accountName : null}
                        onChange={e => {
                            const rawDeviceId = e.target.dataset.deviceid;
                            if(rawDeviceId == null || rawDeviceId === '') return onChange([]);
                            // Parse as number for special values and numeric IDs, keep as string for UUIDs
                            const clickedDeviceId = isNaN(+rawDeviceId) ? rawDeviceId : +rawDeviceId;
                            const wasChecked = e.target.dataset.checked === 'yes';
                            if (isSource) {
                                if (wasChecked) {
                                    if (clickedDeviceId === forAnyObject) {
                                        onChange([]);
                                    } else {
                                        if (forAny) {
                                            onChange(options.filter(o => !disableOption(o.device) && o.device && o.value !== clickedDeviceId).map(o => o.value));
                                        } else {
                                            onChange(value.filter(v => v !== clickedDeviceId));
                                        }
                                    }
                                } else {
                                    if (clickedDeviceId === forAnyObject) {
                                        onChange([forAnyObject]);
                                    } else {
                                        onChange([...value, clickedDeviceId]);
                                    }
                                }
                            } else {
                                const possibleConcerningObject = value.filter(v => v === forConcerningObject);
                                if (wasChecked) {
                                    if (clickedDeviceId === forConcerningObject) {
                                        onChange(value.filter(v => v !== forConcerningObject)); 
                                    } else if (clickedDeviceId === forAnyObject) {
                                        onChange(value.filter(v => v === forConcerningObject));
                                    } else {
                                        if (forAny) {
                                            onChange([
                                                ...possibleConcerningObject,
                                                ...options.filter(o => !disableOption(o.device) && o.device && o.value !== clickedDeviceId).map(o => o.value)
                                            ]);
                                        } else {
                                            onChange(value.filter(v => v !== clickedDeviceId));
                                        }
                                    }
                                } else {
                                    if (clickedDeviceId === forAnyObject) {
                                        onChange([forAnyObject, ...possibleConcerningObject]);
                                    } else {
                                        onChange([...value, clickedDeviceId]);
                                    }
                                }
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                fullWidth
                                {...params}
                                label={label || t('devices')}
                            />
                        )}
                        options={options}
                    />
                </FormControl>
            ) : (
                <>
                    {fetched ? <Alert type="warning" dismissable={false}>{t('noDevices')}</Alert> : (
                        <CircularProgress size={16}/>
                    )}
                </>
            )}
        </Flex>
    );
}

const mapDispatchToProps = () => ({});

const mapStateToProps = createStructuredSelector({
    user: selectCurrentUser(),
    devices: selectDevices(),
    fetched: selectDevicesFetched()
});

export default compose(
    connect(mapStateToProps, mapDispatchToProps)
)(DeviceSelector);